import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Lottie from 'lottie-react';
import './PortfolioGrid.css';
// import stalkMarketThumbStatic from '../assets/images/stalk-market-thumb-static.png';
// Fix the animation import using require
// import stalkMarketAnimation from '../assets/animations/stalk-market.json';
import logo from '../assets/logos/Logo.svg';
// Remove the local video import and use an external URL instead
// import showreel from '../assets/videos/showreel.mp4';

// ScrollReveal component to handle intersection observer logic
const ScrollReveal = ({ children, threshold = 0.1, rootMargin = "0px", delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the element enters the viewport
        if (entry.isIntersecting) {
          // Add a delay if specified
          if (delay) {
            setTimeout(() => setIsVisible(true), delay);
          } else {
            setIsVisible(true);
          }
          // Once visible, no need to observe anymore
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin,
        threshold,
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin, delay]);

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: `translateY(${isVisible ? 0 : 30}px)`,
        transition: `opacity 0.8s ease, transform 0.8s ease${delay ? ` ${delay}ms` : ''}`,
      }}
    >
      {children}
    </div>
  );
};

const PortfolioGrid = () => {
  // Animation states for initial load only
  const [headerVisible, setHeaderVisible] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);

  const portfolioItems = [
    {
      id: 1,
      title: 'Stalk Market',
      description: 'Stock market visualized by a stalk market',
      animation: require('../assets/animations/stalk-market.json'),
      path: '/stalk-market',
    },
    {
      id: 2,
      title: 'Promptimizer',
      description: 'Optimize your AI prompts for better results',
      animation: require('../assets/animations/promptimizer.json'),
      path: '/promptimizer',
    },
    {
      id: 3,
      title: 'Hey Mom!',
      description: 'Connect with your loved ones',
      animation: require('../assets/animations/hey-mom.json'),
      path: '/hey-mom',
    }
  ];

  // Initialize animation states for initial page load
  useEffect(() => {
    setTimeout(() => setHeaderVisible(true), 300);
    setTimeout(() => setHeroVisible(true), 900);
  }, []);

  return (
    <>
      <header 
        className="site-header"
        style={{
          opacity: headerVisible ? 1 : 0,
          transform: `translateY(${headerVisible ? 0 : -20}px)`,
          transition: 'opacity 0.8s ease, transform 0.8s ease'
        }}
      >
        <div className="header-content">
          <div className="logo-container">
            <img src={logo} alt="Logo" className="logo" />
          </div>
          <nav className="main-nav">
            <a href="mailto:hilal@penguinsfrompluto.com">CONTACT US</a>
          </nav>
        </div>
      </header>
      
      <div className="hero">
        <div
          style={{
            opacity: heroVisible ? 1 : 0,
            transform: `translateY(${heroVisible ? 0 : 30}px)`,
            transition: 'opacity 1s ease, transform 1s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          <h1>
            EMBED&shy;DED<br />DE&shy;SIGN<br />LEADER&shy;SHIP
          </h1>
          <p 
            className="hero-description"
            style={{
              transitionDelay: '0.3s'
            }}
          >
            We are a team of senior design leaders with experience in top agencies and tech companies. We
            address tactical and strategic product needs by embedding ourselves directly within our
            clients' teams through short- and long-term residencies. We don't see projects as
            transactions. We share ownership of outcomes, ensuring every success is a win for our
            clients, our portfolio, and the world. Our portfolio reflects our commitment to high-impact
            work, ranging from AI-driven platforms, operational tools, and scalable systems for global
            enterprises, scaling startups, and AI SaaS companies.
          </p>
        </div>
      </div>

      {/* Video Section */}
      <ScrollReveal threshold={0.2} rootMargin="0px 0px -100px 0px">
        <section className="showreel-section">
          <div className="showreel-container">
            <div className="vimeo-wrapper">
              <iframe 
                src="https://player.vimeo.com/video/1058263089?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479&dnt=1&transparent=1&autoplay=1&muted=1&loop=1"
                width="100%" 
                height="100%" 
                frameBorder="0" 
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media" 
                title="PFP Reel"
                className="vimeo-iframe"
              ></iframe>
            </div>
          </div>
        </section>
      </ScrollReveal>
      
      <div className="portfolio-container">
        <ScrollReveal threshold={0.2} rootMargin="0px 0px -100px 0px">
          <h1>
            We love experimen&shy;ting with technolo&shy;gy.<br />Please enjoy our latest experiments with AI.
          </h1>
        </ScrollReveal>

        <div className="portfolio-grid">
          {portfolioItems.map((item, index) => (
            <ScrollReveal key={item.id} delay={index * 200}>
              <Link to={item.path} className="portfolio-link">
                <div className="portfolio-item">
                  <div className="lottie-container">
                    <LottieItem 
                      animation={item.animation}
                      alt={item.title} 
                    />
                  </div>
                  
                  <h3 className="project-title">{item.title} <span className="project-description">{item.description}</span></h3>             
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </div>

      {/* Footer */}
      <ScrollReveal threshold={0.1} rootMargin="0px 0px -50px 0px">
        <footer className="site-footer">
          <div className="footer-content">
            <div className="copyright">
              @penguinsfrompluto all rights reserved
            </div>
            <div className="social-links">
              <a href="https://www.instagram.com/penguinsfrompluto?igsh=MTkxZ3o5a2dhaGhvZQ%3D%3D&utm_source=qr" className="social-link instagram" target="_blank" rel="noopener noreferrer">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="white"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/penguins-from-pluto?trk=public_post_feed-actor-name" className="social-link linkedin" target="_blank" rel="noopener noreferrer">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" fill="white"/>
                </svg>
              </a>
            </div>
          </div>
        </footer>
      </ScrollReveal>
    </>
  );
};

// Lottie component with hover handling
const LottieItem = ({ animation, alt }) => {
  const [isHovering, setIsHovering] = useState(false);
  const lottieRef = useRef(null);
  
  // Initialize to the last frame when component mounts
  React.useEffect(() => {
    if (lottieRef.current) {
      // Get total frames and set to last frame
      const totalFrames = lottieRef.current.getDuration(true);
      lottieRef.current.goToAndStop(totalFrames - 1, true);
    }
  }, []);
  
  // When hovering starts, go to first frame and play
  const handleMouseEnter = () => {
    setIsHovering(true);
    if (lottieRef.current) {
      lottieRef.current.goToAndPlay(0, true);
    }
  };
  
  // When hovering ends, go to last frame and stop
  const handleMouseLeave = () => {
    setIsHovering(false);
    if (lottieRef.current) {
      // Get total frames and set to last frame
      const totalFrames = lottieRef.current.getDuration(true);
      lottieRef.current.goToAndStop(totalFrames - 1, true);
    }
  };

  return (
    <div 
      className="lottie-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animation}
        loop={true}
        autoplay={false}
        className="lottie-animation"
        style={{ 
          width: '100%',
          borderRadius: '6px',
          overflow: 'hidden' 
        }}
        renderSettings={{
          preserveAspectRatio: 'xMidYMid meet',
          clearCanvas: false
        }}
      />
    </div>
  );
};

export default PortfolioGrid; 